import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { absUrl } from "@/lib/config";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import DynamicFields from "@/components/DynamicFields";
import { LabelPicker } from "@/components/ProjectLabels";
import { SubtasksSection, WatchersSection } from "@/components/TaskSubtasksWatchers";
import { X, Paperclip, Trash, DownloadSimple, PaperPlaneTilt, User as UserIcon, At } from "@phosphor-icons/react";

const PRIORITY_STYLES = {
  low: "bg-slate-100 text-slate-700 border-slate-300",
  medium: "bg-blue-50 text-blue-700 border-blue-300",
  high: "bg-orange-50 text-orange-700 border-orange-300",
  urgent: "bg-red-50 text-red-700 border-red-400",
};

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Mention-aware textarea.
 * Typing '@' opens a popup with matching names; picking one inserts "@Name "
 * and records the user id in `mentions`.
 */
function MentionTextarea({ value, onChange, mentions, setMentions, candidates, placeholder, onSubmit }) {
  const taRef = useRef(null);
  const [pop, setPop] = useState({ open: false, query: "", start: 0 });
  const [hover, setHover] = useState(0);

  const filtered = pop.open
    ? candidates.filter(u => u.name.toLowerCase().includes(pop.query.toLowerCase())).slice(0, 6)
    : [];

  const handleChange = (e) => {
    const v = e.target.value;
    const caret = e.target.selectionStart || v.length;
    onChange(v);
    // find @ preceding caret (word boundary)
    const before = v.slice(0, caret);
    const m = before.match(/(^|\s)@([\w.-]*)$/);
    if (m) {
      const start = caret - m[2].length - 1;
      setPop({ open: true, query: m[2], start });
      setHover(0);
    } else {
      setPop({ open: false, query: "", start: 0 });
    }
  };

  const pick = (u) => {
    const v = value;
    const before = v.slice(0, pop.start);
    const after = v.slice(taRef.current.selectionStart || v.length);
    const inserted = `@${u.name.replace(/\s+/g, "_")} `;
    const next = before + inserted + after;
    onChange(next);
    if (!mentions.includes(u.id)) setMentions([...mentions, u.id]);
    setPop({ open: false, query: "", start: 0 });
    setTimeout(() => {
      if (taRef.current) {
        const p = (before + inserted).length;
        taRef.current.focus();
        taRef.current.setSelectionRange(p, p);
      }
    }, 0);
  };

  const onKeyDown = (e) => {
    if (pop.open && filtered.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHover(h => (h + 1) % filtered.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHover(h => (h - 1 + filtered.length) % filtered.length); }
      else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(filtered[hover]); }
      else if (e.key === "Escape") { setPop({ open: false, query: "", start: 0 }); }
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={3}
        className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
        data-testid="comment-textarea"
      />
      {pop.open && filtered.length > 0 && (
        <div className="absolute z-50 bg-white border border-border shadow-lg w-64 max-h-56 overflow-y-auto" style={{ top: "100%", left: 8 }} data-testid="mention-popup">
          {filtered.map((u, i) => (
            <button
              key={u.id}
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              onMouseEnter={() => setHover(i)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 ${hover === i ? "bg-muted" : ""}`}
              data-testid={`mention-option-${u.id}`}
            >
              <At size={12} weight="bold" className="text-[#7C3AED]" />
              <span className="font-semibold">{u.name}</span>
              <span className="text-muted-foreground text-xs ml-auto">{u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TaskDetail({ taskId, project, users, onClose, onChanged }) {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const [t, setT] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [commentMentions, setCommentMentions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const canManage = user.role !== "employee" || (project?.members || []).includes(user.id);

  // Assignee/watcher/mention candidates.
  // - Prefer users that are project members (or any non-employee role — managers/HR/admins
  //   can implicitly be assigned even if not explicit members).
  // - Additionally, always include the currently-assigned user + any watchers, so their
  //   name still renders on the drawer even after the admin removed them from
  //   the project's members list.
  const _memberSet = new Set([...(project?.members || [])]);
  const _extra = new Set([
    ...(t?.assignee_id ? [t.assignee_id] : []),
    ...((t?.watchers) || []),
  ]);
  const projectMembers = users.filter(u =>
    _memberSet.has(u.id) || u.role !== "employee" || _extra.has(u.id)
  );

  const load = async () => {
    try {
      const [tr, cr] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/comments`),
      ]);
      setT(tr.data);
      setComments(cr.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { if (taskId) load(); }, [taskId]);

  // Debounced patch for custom_fields (avoid a PATCH on every keystroke)
  const cfDebounceRef = useRef(null);
  const patchCustomFields = (nextCF) => {
    // optimistic local update
    setT(prev => (prev ? { ...prev, custom_fields: nextCF } : prev));
    if (cfDebounceRef.current) clearTimeout(cfDebounceRef.current);
    cfDebounceRef.current = setTimeout(() => {
      patch({ custom_fields: nextCF });
    }, 700);
  };

  const patch = async (partial) => {
    setSaving(true);
    setError("");
    try {
      const r = await api.patch(`/tasks/${taskId}`, partial);
      setT(prev => ({ ...prev, ...r.data }));
      onChanged && onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true); setError("");
    try {
      const ids = [];
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      const existing = t.attachments || [];
      await patch({ attachments: [...existing, ...ids] });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (fid) => {
    const next = (t.attachments || []).filter(x => x !== fid);
    await patch({ attachments: next });
    await load();
  };

  const submitComment = async () => {
    if (!commentBody.trim()) return;
    setError("");
    try {
      await api.post(`/tasks/${taskId}/comments`, {
        body: commentBody,
        mentions: commentMentions,
      });
      setCommentBody("");
      setCommentMentions([]);
      const cr = await api.get(`/tasks/${taskId}/comments`);
      setComments(cr.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteComment = async (cid) => {
    const ok = await confirm({
      title: "Delete this comment?",
      description: "The comment will be removed permanently.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    await api.delete(`/tasks/${taskId}/comments/${cid}`);
    setComments(cs => cs.filter(c => c.id !== cid));
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="task-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white border-l border-border overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="eyebrow">{t?.task_type?.toUpperCase() || "TASK"}</div>
            <div className="text-xs text-muted-foreground truncate">{project?.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-drawer"><X size={18} weight="bold" /></button>
        </div>

        {!t ? (
          <div className="p-12 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="eyebrow block mb-2">Title</label>
              <input
                defaultValue={t.title}
                onBlur={(e) => e.target.value !== t.title && patch({ title: e.target.value })}
                className="w-full border border-border px-3 py-2 font-display text-xl font-bold focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="drawer-title"
              />
            </div>

            {/* Description */}
            <div>
              <label className="eyebrow block mb-2">Description</label>
              <textarea
                defaultValue={t.description || ""}
                rows={4}
                onBlur={(e) => e.target.value !== (t.description || "") && patch({ description: e.target.value })}
                className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="drawer-description"
              />
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="eyebrow block mb-2">Status</label>
                <select
                  value={t.status}
                  onChange={(e) => patch({ status: e.target.value })}
                  className="w-full border border-border px-3 py-2 text-sm bg-white"
                  data-testid="drawer-status"
                >
                  {(project?.lanes || []).map(l => (
                    <option key={l.key} value={l.key}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-2">Type</label>
                <select value={t.task_type || "task"} onChange={(e) => patch({ task_type: e.target.value })} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="drawer-type">
                  <option value="task">Task</option><option value="story">Story</option><option value="bug">Bug</option><option value="epic">Epic</option>
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-2">Priority</label>
                <select value={t.priority} onChange={(e) => patch({ priority: e.target.value })} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="drawer-priority">
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-2">Points</label>
                <input
                  type="number" min="0"
                  defaultValue={t.points || 0}
                  onBlur={(e) => Number(e.target.value) !== (t.points || 0) && patch({ points: Number(e.target.value) })}
                  className="w-full border border-border px-3 py-2 text-sm"
                  data-testid="drawer-points"
                />
              </div>
              <div>
                <label className="eyebrow block mb-2">Assignee</label>
                <select value={t.assignee_id || ""} onChange={(e) => patch({ assignee_id: e.target.value || null })} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="drawer-assignee">
                  <option value="">Unassigned</option>
                  {projectMembers.map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
                </select>
              </div>
              <div>
                <label className="eyebrow block mb-2">Due date</label>
                <input
                  type="date"
                  defaultValue={t.due_date || ""}
                  onBlur={(e) => e.target.value !== (t.due_date || "") && patch({ due_date: e.target.value || null })}
                  className="w-full border border-border px-3 py-2 text-sm"
                  data-testid="drawer-due"
                />
              </div>
            </div>

            {/* Badges preview */}
            <div className="flex flex-wrap gap-2 text-[10px] font-mono-mini uppercase">
              <span className={`border px-1.5 py-0.5 ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span>
              {t.points > 0 && <span className="border border-[#0A0A0A] bg-[#0A0A0A] text-white px-1.5 py-0.5">{t.points} pt</span>}
              <span className="border border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF] px-1.5 py-0.5">{t.task_type}</span>
            </div>

            {/* Custom fields (driven by /api/settings/task-forms/{task_type}) */}
            <DynamicFields
              taskType={t.task_type || "task"}
              value={t.custom_fields || {}}
              onChange={patchCustomFields}
              users={projectMembers}
              projectId={t.project_id}
            />

            {/* Labels */}
            <LabelPicker
              projectId={t.project_id}
              value={t.label_ids || []}
              onChange={(v) => patch({ label_ids: v })}
            />

            {/* Sub-tasks */}
            <SubtasksSection
              task={t}
              currentUserId={user.id}
              onPatch={patch}
              onChanged={onChanged}
            />

            {/* Watchers */}
            <WatchersSection
              task={t}
              users={projectMembers}
              currentUserId={user.id}
              onPatch={patch}
            />

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow">Attachments ({(t.attachment_details || []).length})</div>
                <label className="text-[10px] font-mono-mini uppercase cursor-pointer hover:text-[#002FA7]">
                  <input
                    type="file" multiple className="hidden"
                    onChange={(e) => uploadFiles(e.target.files)}
                    data-testid="attachment-upload"
                  />
                  <span className="inline-flex items-center gap-1">
                    <Paperclip size={12} weight="bold" /> {uploading ? "Uploading…" : "Add file"}
                  </span>
                </label>
              </div>
              <div className="border border-border">
                {(t.attachment_details || []).length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">No files attached.</div>
                ) : (
                  (t.attachment_details || []).map(a => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0" data-testid={`attachment-${a.id}`}>
                      <Paperclip size={14} className="shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold truncate">{a.name}</div>
                        <div className="text-[10px] font-mono-mini text-muted-foreground">{fmtSize(a.size)}</div>
                      </div>
                      <a href={absUrl(a.url)} target="_blank" rel="noreferrer" className="p-1 hover:text-[#002FA7]" title="Download">
                        <DownloadSimple size={14} />
                      </a>
                      {canManage && (
                        <button onClick={() => removeAttachment(a.id)} className="p-1 hover:text-[#FF3B30]" title="Remove" data-testid={`remove-attachment-${a.id}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Comments */}
            <div>
              <div className="eyebrow mb-2">Comments ({comments.length})</div>
              <div className="space-y-3 mb-4">
                {comments.map(c => (
                  <div key={c.id} className="border border-border p-3" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {c.author_avatar ? (
                        <img src={absUrl(c.author_avatar)} alt="" className="w-6 h-6 object-cover" />
                      ) : (
                        <div className="w-6 h-6 bg-[#0A0A0A] text-white text-[10px] flex items-center justify-center font-bold">
                          {c.author_name?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                      <div className="text-xs font-semibold">{c.author_name}</div>
                      <div className="text-[10px] font-mono-mini text-muted-foreground ml-auto">
                        {c.created_at?.slice(0, 16).replace("T", " ")}
                      </div>
                      {(c.author_id === user.id || ["admin","manager"].includes(user.role)) && (
                        <button onClick={() => deleteComment(c.id)} className="p-1 text-muted-foreground hover:text-[#FF3B30]" data-testid={`delete-comment-${c.id}`}>
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{c.body}</div>
                    {c.mentions?.length > 0 && (
                      <div className="mt-1 text-[10px] font-mono-mini text-[#7C3AED]">
                        <At size={10} weight="bold" className="inline mr-0.5" />
                        {c.mentions.length} mentioned
                      </div>
                    )}
                  </div>
                ))}
                {comments.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-4">Be the first to comment.</div>
                )}
              </div>
              <MentionTextarea
                value={commentBody}
                onChange={setCommentBody}
                mentions={commentMentions}
                setMentions={setCommentMentions}
                candidates={projectMembers}
                placeholder="Add a comment. Use @ to mention a teammate…"
                onSubmit={submitComment}
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-[10px] font-mono-mini text-muted-foreground">
                  {commentMentions.length > 0 ? `${commentMentions.length} mentioned · ` : ""}⌘/Ctrl + Enter to post
                </div>
                <button onClick={submitComment} disabled={!commentBody.trim()} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="submit-comment">
                  <PaperPlaneTilt size={14} weight="bold" /> Comment
                </button>
              </div>
            </div>

            {error && <div className="text-xs text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
            {saving && <div className="text-[10px] font-mono-mini text-muted-foreground text-right">Saving…</div>}

            {/* Meta footer */}
            <div className="text-[10px] font-mono-mini text-muted-foreground pt-4 border-t border-border">
              {t.created_by_name && <>Created by {t.created_by_name} · </>}
              {t.created_at?.slice(0, 10)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
